import React, { useState } from "react";
import styled from "styled-components";
import { Select, Typography } from "antd";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import ReactPaginate from "react-paginate";
import { blueColor } from "../styled/Theme";

const { Text } = Typography;

const PAGE_SIZE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
];

export type PaginationProps = {
  count?: number;
  filters?: Record<string, any>;
  onFilterChange: (filters: Record<string, any>) => void;
  loading?: boolean;
  defaultPageSize?: number;
  showPageSizeSelector?: boolean;
};

const Pagination = ({
  count,
  filters = {},
  onFilterChange,
  loading,
  defaultPageSize = 10,
  showPageSizeSelector = true,
}: PaginationProps) => {
  const [pageSize, setPageSize] = useState<number>(
    filters?.page_size || defaultPageSize,
  );

  if (loading || count == null || count === 0) return null;

  const currentPage = filters?.page || 1;
  const effectivePageSize = filters?.page_size || pageSize;
  const pageCount = Math.ceil(count / effectivePageSize);

  const startItem = (currentPage - 1) * effectivePageSize + 1;
  const endItem = Math.min(currentPage * effectivePageSize, count);

  return (
    <PaginationBar>
      <PaginationLeft>
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          {startItem}–{endItem} of {count}
        </Text>
        {showPageSizeSelector && (
          <Select
            size="small"
            options={PAGE_SIZE_OPTIONS}
            value={effectivePageSize}
            onChange={(val) => {
              setPageSize(val);
              onFilterChange({ ...filters, page: 1, page_size: val });
            }}
            style={{ width: 70 }}
          />
        )}
      </PaginationLeft>

      {pageCount > 1 && (
        <ReactPaginate
          breakLabel="..."
          previousLabel={
            <NavBtn>
              <ChevronLeft sx={{ fontSize: 15 }} />
            </NavBtn>
          }
          nextLabel={
            <NavBtn>
              <ChevronRight sx={{ fontSize: 15 }} />
            </NavBtn>
          }
          onPageChange={({ selected }) =>
            onFilterChange({ ...filters, page: selected + 1 })
          }
          pageRangeDisplayed={3}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          renderOnZeroPageCount={null}
          containerClassName="op-pagination"
          pageClassName="op-page-item"
          pageLinkClassName="op-page-link"
          previousClassName="op-page-item op-nav"
          previousLinkClassName="op-page-link op-nav-link"
          nextClassName="op-page-item op-nav"
          nextLinkClassName="op-page-link op-nav-link"
          activeClassName="op-active"
          forcePage={currentPage - 1}
          breakClassName="op-page-item"
          breakLinkClassName="op-page-link"
        />
      )}
    </PaginationBar>
  );
};

export default Pagination;

/* ─── Styled components ──────────────────────────────────────────────────── */

export const PaginationBar = styled.div`
  padding: 8px 16px;
  border-top: 1px solid #f1f5fb;
  background: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .op-pagination {
    display: flex;
    align-items: center;
    gap: 3px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .op-page-item .op-page-link {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    background: transparent;
    border: 1px solid transparent;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1;

    &:hover {
      background: #f1f5f9;
      color: ${blueColor};
      border-color: #e2e8f0;
    }
  }

  .op-active .op-page-link {
    background: ${blueColor} !important;
    color: #fff !important;
    border-color: ${blueColor} !important;
    font-weight: 700;
    box-shadow: 0 2px 8px ${blueColor}30;
  }

  .op-nav .op-nav-link {
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    padding: 0 7px;

    &:hover {
      background: ${blueColor};
      color: #fff;
      border-color: ${blueColor};
    }
  }

  .disabled .op-page-link,
  .disabled .op-nav-link {
    opacity: 0.35;
    pointer-events: none;
  }
`;

const PaginationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavBtn = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;
